/**
 * Client contract for the development brain (docs/kodulehe-arendusaju-plaan.md).
 *
 * Mirrors `apps/api/src/modules/ariregister/lib/brain/types.ts` by hand, the way
 * `siteScan.ts` and `scanLeads.ts` next door do. Nothing validates this at
 * runtime, so a field the API stops sending arrives as `undefined` rather than
 * as an error - keep the two files edited together.
 *
 * DOMAIN-KEYED, like the scanner and the leads table. A lead can have no
 * register code at all, and those are exactly the sites this tool was built for.
 */
import { api } from "./api";
import type { BrainEmailDraft } from "./siteBrainEmail";
import type { BrainCallScript, BrainCallTouch } from "./siteBrainScript";

export type BrainStatus = "running" | "done" | "error";
export type BrainSeverity = "critical" | "warning" | "info" | "good";
export type BrainSource = "crawl" | "links" | "probe" | "content" | "vision";

/** One thing we know, with the proof the operator can open. */
export interface BrainFinding {
  id: string;
  /** Stable slug, also the i18n key suffix: `brain.finding.<kind>`. */
  kind: string;
  severity: BrainSeverity;
  source: BrainSource;
  measured: boolean;
  vars?: Record<string, string | number>;
  /**
   * Sentence written by an AI pass about this one site, shown as-is.
   *
   * Only `content` and `vision` findings carry it, and they have to: a model's
   * sentence about one particular website cannot be a translation key. Absent
   * means the catalogue renders it, exactly as the measured findings do.
   */
  text?: string;
  evidence: { url?: string; value?: string; screenshotKey?: string };
}

export interface BrainCrawlPage {
  url: string;
  status: number;
  title: string | null;
  metaDescription: string | null;
  h1: string[];
  words: number;
  bytes: number;
  digest: string;
  source: "home" | "sitemap" | "nav";
  /**
   * What the malware rules saw on this page, absent on a crawl stored before
   * they ran. `read: false` is a bot wall or an unwalkable tree, which is NOT a
   * clean page: three states, and only one of them is good news. Mirrored here
   * although nothing renders it yet, because the last time a field was left out
   * of a mirror it stayed missing until it caused a bug.
   */
  malware?: { read: boolean; signs: string[]; sample: string | null };
}

export interface BrainCrawl {
  runSeq: number;
  at: string;
  startUrl: string;
  finalUrl: string;
  discovery: "sitemap" | "nav" | "home-only";
  pages: BrainCrawlPage[];
  skipped: number;
}

export interface BrainLinkIssue {
  url: string;
  verdict: "broken" | "unverifiable";
  status: number | null;
  code: string;
  scope: "internal" | "external";
  text: string | null;
  foundOn: string[];
}

export interface BrainLinks {
  runSeq: number;
  at: string;
  total: number;
  checked: number;
  /** Definitely dead. The only bucket that may be quoted to a prospect. */
  broken: BrainLinkIssue[];
  /** Walls, timeouts, rate limits. Shown to the operator, never sold. */
  unverifiable: BrainLinkIssue[];
}

export type BrainViewport = "desktop" | "mobile";

/** One element the layout probe measured, with a short locator. */
export interface BrainProbeEntry {
  at: string;
  text?: string;
  [key: string]: unknown;
}

/** What a real browser measured after laying the page out. */
export interface BrainProbeMeasurement {
  ok: boolean;
  error?: string;
  viewport?: { width: number; height: number };
  documentWidth?: number;
  scrollsSideways?: boolean;
  pageHeight?: number;
  overflowText?: BrainProbeEntry[];
  offCanvas?: BrainProbeEntry[];
  tinyTargets?: BrainProbeEntry[];
  smallText?: BrainProbeEntry[];
  images?: string[];
}

export interface BrainLayout {
  runSeq: number;
  at: string;
  viewports: Partial<Record<BrainViewport, BrainProbeMeasurement>>;
  /** Why a viewport gave nothing. Named, so the card can explain the gap. */
  failures: { viewport: BrainViewport; reason: string }[];
}

export interface BrainShot {
  viewport: BrainViewport;
  key: string;
  /** Filled in by the API from the key. Absent when storage is not configured. */
  url?: string;
  width: number;
  height: number;
  bytes: number;
  browserMs: number | null;
}

export interface BrainShots {
  runSeq: number;
  at: string;
  shots: BrainShot[];
  browserMs: number;
}

/** What the site says about itself, read out of its own pages. */
export interface BrainContent {
  runSeq: number;
  at: string;
  offering: string;
  audience: string;
  /** Null when the site asks the visitor to do nothing at all. */
  primaryCta: string | null;
  observations: { kind: string; severity: BrainSeverity; page: string; detail: string }[];
  model: string;
}

/**
 * One checklist answer from the picture.
 *
 * No severity and no numbers, deliberately: the model looks, the code judges,
 * and every number in this tool comes from the layout probe.
 */
export interface BrainVisionObservation {
  kind: string;
  answer: "yes" | "no" | "unclear";
  note: string;
  viewport: BrainViewport;
}

export interface BrainVision {
  runSeq: number;
  at: string;
  observations: BrainVisionObservation[];
  viewports: BrainViewport[];
  model: string;
}

/** Register facts the synthesis reasoned about, stored as it saw them. */
export interface BrainFacts {
  name: string | null;
  regCode: string | null;
  status: string | null;
  activity: string | null;
  /** Null is "no figure filed", NOT "no turnover". */
  revenueEur: number | null;
  employees: number | null;
  year: number | null;
  webNeed: number | null;
}

/** The three answers the synthesis is allowed to reach. */
export type BrainFitDecision = "go" | "hold" | "no";

/** Should this company be sold to at all, and with which offer. */
export interface BrainFit {
  decision: BrainFitDecision;
  segment: "A" | "C" | null;
  reasons: string[];
  /** Slugs from a closed list: `brain.stopFlag.<slug>`. */
  stopFlags: string[];
  demoCandidate: boolean;
}

/** One thing to say, and why it costs the owner money. */
export interface BrainArgument {
  id: string;
  /** Findings it rests on. The server guarantees every one of them resolves. */
  findingIds: string[];
  claim: string;
  businessImpact: string;
}

export interface BrainSynthesis {
  runSeq: number;
  at: string;
  fit: BrainFit;
  /** Finding ids, worst first. */
  order: string[];
  arguments: BrainArgument[];
  facts: BrainFacts;
  /** Arguments the server threw away for citing findings that do not exist. */
  dropped: number;
  model: string;
}

export interface SiteBrain {
  domain: string;
  regCode: string | null;
  url: string | null;
  runSeq: number;
  status: BrainStatus;
  startedAt: string;
  /** Who started this run. Same column, same three cases, as the list row. */
  startedBy: BrainStarter;
  /**
   * The starter's name, or null.
   *
   * Sent only for a run somebody ELSE started: the reader's own run is drawn as
   * "you", and this row is polled every three seconds while a run is in flight.
   */
  startedByName: string | null;
  finishedAt: string | null;
  error: string | null;
  /** Stage ids that failed without killing the run. */
  degraded: string[];
  crawl: BrainCrawl | null;
  links: BrainLinks | null;
  layout: BrainLayout | null;
  shots: BrainShots | null;
  content: BrainContent | null;
  vision: BrainVision | null;
  synthesis: BrainSynthesis | null;
  /** The stored letter, when this run has one. See `siteBrainEmail.ts`. */
  emailDraft: BrainEmailDraft | null;
  /** The stored call script, when this run has one. See `siteBrainScript.ts`. */
  callScript: BrainCallScript | null;
  /**
   * That a call was made, and what came of it.
   *
   * Read from columns, so it survives a re-run - unlike the script above, which
   * is a stage and is generation-filtered. A call is a thing that happened.
   */
  callTouch: BrainCallTouch | null;
  /** The sendable page, when one has been built. Survives a re-run on purpose. */
  report: BrainReportInfo | null;
  /** What this run has cost so far. Columns, so a re-run does not zero it. */
  cost: BrainCost | null;
  /** When to come back to this company, and why. Also a column. */
  plan: BrainPlan | null;
  /** When the retention sweep took the pictures and the report, or null. */
  purgedAt: string | null;
  findings: BrainFinding[];
}

/**
 * What one run has cost, in the two currencies it is actually paid in.
 *
 * Two figures rather than one total: model tokens are money against the
 * provider account, browser milliseconds are an allowance shared with the
 * template thumbnails, and adding them would be arithmetic on unlike units.
 */
export interface BrainCost {
  model: string | null;
  tokensIn: number;
  tokensOut: number;
  /**
   * Estimated cents, exact fraction. NULL when the model is not in the price
   * table - render "unknown", NEVER zero. A free run and a run nobody can price
   * look nothing alike to somebody deciding whether to press the button again.
   */
  cents: number | null;
  browserMs: number | null;
  /** Part of `browserMs` is a flat guess rather than a reported figure. */
  browserMsEstimated: boolean;
}

/** A follow-up an operator set on one company. */
export interface BrainPlan {
  nextTouchAt: string;
  note: string | null;
  /** The date has passed. Not the same as "no date" - that has no plan at all. */
  due: boolean;
  /** Whole days until it is due; negative when it is already past. */
  inDays: number;
}

/** One row of the follow-up queue. */
/**
 * What the last comparison of one site said (R10).
 *
 * ⚠ THE STORED COUNTS, NOT A FRESH DIFF. The card's history tab rebuilds both
 * runs' findings and compares them again, because a sentence somebody says out
 * loud has to come from one rulebook. These numbers were counted once when the
 * run finished, so that a queue can show fifty companies a number without
 * rebuilding fifty companies' findings. They are triage, never a claim to repeat
 * to the company, and they can differ from the tab if a finding rule changed in
 * between.
 */
export interface BrainRepairSummary {
  runSeq: number;
  /** When the compared run finished. */
  at: string;
  previousRunSeq: number;
  fixed: number;
  appeared: number;
  /**
   * The verdict moved with nothing measured moving under it, or null.
   *
   * Unlike the counts above, this is not triage: it is the row saying the pill
   * beside it changed on its own. Null on every comparison written before the
   * server started recording the previous verdict, which is the right answer -
   * a verdict nobody stored cannot be shown to have moved.
   */
  drift: BrainVerdictDrift | null;
}

export interface BrainDueRow {
  domain: string;
  regCode: string | null;
  nextTouchAt: string;
  note: string | null;
  due: boolean;
  inDays: number;
  calledAt: string | null;
  callOutcome: string | null;
  fit: BrainFitDecision | null;
  /** Null when this site has never been measured twice. */
  repaired: BrainRepairSummary | null;
}

/** The sendable report as the card sees it. */
export interface BrainReportInfo {
  token: string;
  builtAt: string;
  /** Fetches. Soft evidence: a prefetching mail client opens it too. */
  opens: number;
  firstOpenAt: string | null;
  lastOpenAt: string | null;
}

/**
 * How this company compares with its own field (API plan section 15).
 *
 * Counts only. There is deliberately no company or domain in this shape and
 * there must not be one: the whole point of the aggregate is that a prospect
 * never learns which of their neighbours we found something on.
 */
export interface BrainPeerComparison {
  /** The deterministic finding kind. Matches `BrainFinding.kind`. */
  kind: string;
  peersWith: number;
  /** Peers that could answer for this kind. Never how many matched the field. */
  peersTotal: number;
}

export interface BrainPeerVerdict {
  level: "exact" | "class" | "group" | "division";
  peersRead: number;
  comparisons: BrainPeerComparison[];
}

/**
 * Which measurement a defect came from (R8).
 *
 * The card needs these by name, not as a count, because a comparison that could
 * not cover one of them has to say which one: "the layout was not measured in
 * both runs" is a sentence an operator can act on, and a missing number is not.
 */
export type BrainFindingFamily = "links" | "crawl" | "layout";

/**
 * What changed between one run and the run it was compared with.
 *
 * `fixed` and `appeared` are the findings themselves, so the card renders them
 * with the same catalogue sentence the findings tab uses. `stayed` is a number,
 * because that list is already on screen.
 *
 * `withheld` is the honest part. A defect can vanish without being repaired (a
 * degraded stage, a budget that ran out, a layout nobody could measure), so any
 * family the two runs cannot both stand behind IN FULL is left out of the lists
 * AND named here. A comparison with a silent hole in it reads as a complete one.
 *
 * `linksCompared` is what keeps that honesty from becoming silence. Dead links
 * are compared one address at a time - over the addresses both runs actually got
 * an answer about - so the link family can be incomplete and a repaired link
 * still provable. Read the two together: with `withheld` naming links and a
 * non-zero `linksCompared`, the true sentence is "only these addresses were
 * compared", not "the links were left out".
 */
/**
 * The verdict moved and nothing measured moved with it.
 *
 * The comparison beside it is a ruler; the verdict is a model's opinion, rerun
 * from scratch every time and free to land somewhere else on identical input.
 * Production showed the two are not the same kind of statement: `romu.ee` runs 3
 * and 4 measured the same four defects and the same 59 addresses, and the pill
 * still walked from "hold" to "go".
 */
export interface BrainVerdictDrift {
  from: BrainFitDecision;
  to: BrainFitDecision;
}

export interface BrainRunDelta {
  previousRunSeq: number;
  previousAt: string;
  fixed: BrainFinding[];
  appeared: BrainFinding[];
  stayed: number;
  withheld: BrainFindingFamily[];
  /** Addresses both runs answered for. Zero means no link claim is possible. */
  linksCompared: number;
  /** Null unless the verdict moved over a silent ruler. */
  verdictDrift: BrainVerdictDrift | null;
}

/** One finished run of one domain, as the history tab draws it. */
export interface BrainHistoryRun {
  runSeq: number;
  status: "done" | "error";
  /** Short machine code, translated as `brain.error.<code>`. */
  error: string | null;
  startedAt: string;
  finishedAt: string;
  degraded: string[];
  startedBy: BrainStarter;
  startedByName: string | null;
  fit: BrainFitDecision | null;
  /** Measured defects only. The AI passes are not kept in the history. */
  findings: BrainFinding[];
  measured: BrainFindingFamily[];
  /** Null on the oldest kept run, and on a run that measured nothing. */
  delta: BrainRunDelta | null;
}

export interface BrainHistory {
  domain: string;
  /** Runs kept per domain, so the list can say it is capped. */
  keep: number;
  /** Newest first. */
  runs: BrainHistoryRun[];
}

/** What a list row needs to draw a badge, returned inline with the lead. */
export interface BrainBadge {
  domain: string;
  status: BrainStatus;
  finishedAt: string | null;
  findings: number;
  /** The fit verdict, so triage can skip a company the brain already said no to. */
  fit: BrainFitDecision | null;
}

export interface BrainStarted {
  ok: boolean;
  started: boolean;
  domain: string;
  runSeq: number;
}

/**
 * Bare host for the URL path.
 *
 * Callers hand this either a domain (from a lead row) or the register's whole
 * `webUrl`, and the scheme and path have to come off HERE rather than on the
 * server: an encoded slash inside a path segment is rejected or silently
 * rewritten by plenty of proxies, so `%2F` would work locally and 404 in
 * production. Only the transport is normalised - `www.` and any subdomain are
 * left alone, because the API decides which part is the key and which part is
 * the address to fetch, and stripping a `shop.` here would point the crawl at
 * the wrong site.
 */
export function brainKey(input: string): string {
  const raw = input.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  return raw.split(/[/?#]/)[0] ?? "";
}

const key = (domain: string): string => encodeURIComponent(brainKey(domain));

/** The stored run. 404 when this site has never been run - not an error. */
export const getSiteBrain = (domain: string): Promise<SiteBrain> =>
  api.get<SiteBrain>(`/admin/ariregister/brain/${key(domain)}`);

/**
 * The field comparison, or null.
 *
 * Its own request rather than a field on the run: the run is polled while it is
 * in flight, and this answer has nothing to do with the run's progress. Null is
 * the ordinary answer for a company whose field we have not scanned enough of.
 */
export const getBrainPeers = (domain: string): Promise<{ peers: BrainPeerVerdict | null }> =>
  api.get<{ peers: BrainPeerVerdict | null }>(`/admin/ariregister/brain/${key(domain)}/peers`);

/**
 * The last twelve runs of this site, newest first, each compared with the one
 * before it.
 *
 * 200 and an empty list for a domain nothing has ever measured, where the run
 * itself answers 404. That is not an inconsistency: "we have never looked at this
 * site" is an answer about the site, and there is no missing resource to report.
 */
export const getBrainHistory = (domain: string): Promise<BrainHistory> =>
  api.get<BrainHistory>(`/admin/ariregister/brain/${key(domain)}/history`);

/** Start a run. 409 when one is already in flight for this domain. */
export const runSiteBrain = (domain: string): Promise<BrainStarted> =>
  api.post<BrainStarted>(`/admin/ariregister/brain/${key(domain)}/run`, {});

/**
 * Think again over the pages we already have.
 *
 * Three model calls and NO trip back to the prospect's server, which is the
 * point: the expensive half of a run is the crawl and the Cloudflare browser
 * time, and neither of those needs repeating to get a second opinion, to pick
 * up a better prompt, or to finish a run that had no API key the first time.
 */
export const rethinkSiteBrain = (domain: string): Promise<BrainStarted> =>
  api.post<BrainStarted>(`/admin/ariregister/brain/${key(domain)}/rethink`, {});

// ── R6: the follow-up queue and the autopilot ────────────────────────────────

/**
 * Set or clear the follow-up.
 *
 * `nextTouchAt: null` clears it. A malformed date is a 400 rather than a clear:
 * the two are opposite intentions, and turning a typo into a deletion would
 * lose the plan it failed to set.
 */
export const setBrainPlan = (
  domain: string,
  body: { nextTouchAt: string | null; note?: string | null },
): Promise<{ ok: boolean; plan: BrainPlan | null }> =>
  api.put<{ ok: boolean; plan: BrainPlan | null }>(`/admin/ariregister/brain/${key(domain)}/plan`, body);

/** Companies with a follow-up date, soonest first. */
export const listBrainDue = (params: { limit?: number; onlyDue?: boolean } = {}): Promise<{
  rows: BrainDueRow[];
  due: number;
}> => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.onlyDue) q.set("onlyDue", "1");
  const qs = q.toString();
  return api.get(`/admin/ariregister/brain/due${qs ? `?${qs}` : ""}`);
};

// -- R7: the brain's own list -----------------------------------------------

/**
 * One line of the brain's own list.
 *
 * Deliberately NOT `SiteBrain`. The list reads what is cheap off the row and
 * never rebuilds a finding, because rebuilding fifty companies' findings to
 * draw fifty lines would make the cheapest page the most expensive request in
 * the feature. Open a row to get the whole thing.
 */
/**
 * Who started a run.
 *
 * "unknown" is a row older than the column that records this, and it is NOT the
 * autopilot: telling somebody a machine ran a company they ran themselves last
 * week is a small lie the interface has no need to tell. The server parses the
 * stored column; this side only reads the three cases.
 */
export type BrainStarter = { kind: "auto" } | { kind: "user"; id: string } | { kind: "unknown" };

export interface BrainListRow {
  domain: string;
  regCode: string | null;
  url: string | null;
  status: BrainStatus;
  error: string | null;
  degraded: string[];
  startedAt: string;
  finishedAt: string | null;
  fit: BrainFitDecision | null;
  arguments: number;
  nextTouchAt: string | null;
  calledAt: string | null;
  callOutcome: string | null;
  hasReport: boolean;
  startedBy: BrainStarter;
  /** Resolved from the platform users on read; null when nobody could be found. */
  startedByName: string | null;
  /**
   * What the last comparison of this site said, or null when there has never
   * been one.
   *
   * The same summary the follow-up queue draws, from the same stored counts. The
   * two lists showing different things about one company was the gap this
   * closes.
   */
  repaired: BrainRepairSummary | null;
}

export interface BrainListPage {
  rows: BrainListRow[];
  /** Hand back as `cursor` for the next page; null when this was the last. */
  nextCursor: string | null;
  total: number;
}

/**
 * Companies the brain has looked at, newest activity first.
 *
 * One row per DOMAIN, not per run: a new run overwrites the row it is for, so
 * there is no history to page through. Whatever the UI calls this list, it must
 * not call it a run log.
 */
/**
 * What has been DONE about a company. Three separate questions, not three values
 * of one state: a company can be both called and planned, and a single-valued
 * state would have to pick one to show and be wrong about the other.
 */
export type BrainWorked = "untouched" | "called" | "planned";

export interface BrainListParams {
  limit?: number;
  cursor?: string | null;
  status?: BrainStatus | null;
  q?: string | null;
  /** Verdicts to keep. Empty means every verdict, including rows with none. */
  fit?: BrainFitDecision[];
  worked?: BrainWorked | null;
}

export const listBrainRuns = (params: BrainListParams = {}): Promise<BrainListPage> => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.status) q.set("status", params.status);
  if (params.q?.trim()) q.set("q", params.q.trim());
  // Comma separated rather than a repeated key: a repeated key arrives at the
  // server as a string when there is one of it and an array when there are two,
  // and the route would have to handle both shapes.
  if (params.fit?.length) q.set("fit", params.fit.join(","));
  if (params.worked) q.set("worked", params.worked);
  const qs = q.toString();
  return api.get(`/admin/ariregister/brain-list${qs ? `?${qs}` : ""}`);
};

/** Runs that ended since this operator last looked, and where their bookmark is. */
export interface BrainUnseen {
  done: number;
  /** How many of those this operator started. A subset of `done`, not a peer. */
  mine: number;
  seenAt: string;
}

/**
 * Move this operator's "I have seen the finished runs" bookmark to now.
 *
 * Server-held rather than a note in this browser, because a run ending is an
 * event: it does not resolve itself, so clearing it on a laptop has to clear it
 * on the desktop too. The response is a fresh read, so the caller can use it
 * instead of polling again.
 */
export const markBrainSeen = (): Promise<BrainUnseen> =>
  api.post<BrainUnseen>("/admin/ariregister/brain/seen", {});

export interface BrainAutopilotSettings {
  enabled: boolean;
  intervalMinutes: number;
  batchSize: number;
  rerunAfterDays: number;
  /** Only leads the scanner already scored at or above this are picked. */
  minLeadScore: number;
  /** Share of the day's browser allowance this engine may spend. Never 100. */
  budgetSharePct: number;
}

export interface BrainAutopilotRun {
  id: string;
  trigger: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  picked: number;
  started: number;
  done: number;
  failed: number;
  browserMs: number;
  total: number;
  error: string | null;
  /** Why a batch did nothing, when it did nothing. `jobs`-style enum. */
  note: string | null;
}

export interface BrainAutopilotState {
  settings: BrainAutopilotSettings;
  busy: boolean;
  runs: BrainAutopilotRun[];
  nextRunAt: string | null;
  stats: { due: number; purgeDue: number };
}

export const getBrainAutopilot = (): Promise<BrainAutopilotState> =>
  api.get<BrainAutopilotState>("/admin/ariregister/brain-autopilot");

export const saveBrainAutopilot = (
  patch: Partial<BrainAutopilotSettings>,
): Promise<{ ok: boolean; settings: BrainAutopilotSettings }> =>
  api.put<{ ok: boolean; settings: BrainAutopilotSettings }>("/admin/ariregister/brain-autopilot", patch);

export const runBrainAutopilot = (limit?: number): Promise<{ ok: boolean; started: boolean }> =>
  api.post<{ ok: boolean; started: boolean }>("/admin/ariregister/brain-autopilot/run", limit ? { limit } : {});
